import { useLayoutEffect, useRef, useState } from 'react'
import { FileText, MonitorUp, PenTool, PlayCircle, Users, X } from 'lucide-react'
import type { StageItem, StageItemKind } from '../hooks/useStageContent'

/**
 * The switcher for everything currently being presented.
 *
 * Shown only when there is a choice to make. With one thing on the stage the
 * bar would be a row of exactly one chip that does nothing when pressed, which
 * is worse than no bar: it takes vertical space from the content to tell the
 * viewer something the content already tells them.
 *
 * The tabs scroll when there are more than fit; the controls at the end (Sync
 * everyone, Following) stay put at the end of the row, which is the right edge
 * of the shared panel below it (`endAt`), not out over the participant rail
 * where the host's requests pill sits.
 */

interface StageContentBarProps {
  items: StageItem[]
  activeKey: string | null
  onSelect: (key: string) => void
  /** Moderator only: turn the sync on, or (as its owner) off. */
  canSync: boolean
  /** This client owns the sync in force: the control shows as switched on. */
  syncActive: boolean
  onSync: () => void
  /** This client is on the host's pick and has not chosen otherwise since. */
  following: boolean
  /** Inside someone else's sync: the chip stays and offers no way out. */
  followingLocked: boolean
  onStopFollowing: () => void
  /**
   * "Sync on by <name>", for a moderator who is not the owner, or null. Takes
   * the place of the following chip for them: they need to know who is
   * driving, not just that someone is.
   */
  syncOnByLabel: string | null
  /** The host only: turns off a sync someone else owns. */
  onTurnOffSync?: () => void
  turnOffSyncLabel: string
  syncLabel: string
  /** The owner's control while its sync is on. */
  syncingLabel: string
  followingLabel: string
  /**
   * The shared panel below the row. The row ends where it ends, so the end
   * controls sit over the panel's right edge. Null where the panel spans the
   * row (phones, layouts without a participant rail).
   */
  endAt?: HTMLElement | null
  /**
   * Pixels to keep clear at the end of the row, for whatever floats over its
   * right end (the host's requests pill, a participant's own request chip)
   * when the panel reaches that far. Whichever of this and `endAt` keeps
   * more clear wins.
   */
  endInset?: number
}

// A sync in force wears the selected tab's blue, for everyone in the room
// whichever side of it they are on: it is the room's selection, made for them.
const SYNC_ON_CLASSES = 'bg-primary text-white ring-1 ring-primary'
// The one-off follow (no sync in force) keeps its amber, so the two are told apart.
const FOLLOWING_CLASSES = 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30 hover:bg-amber-500/25'

const ICONS: Record<StageItemKind, typeof MonitorUp> = {
  screen: MonitorUp,
  whiteboard: PenTool,
  document: FileText,
  video: PlayCircle,
}

export function StageContentBar({
  items,
  activeKey,
  onSelect,
  canSync,
  syncActive,
  onSync,
  following,
  followingLocked,
  onStopFollowing,
  syncOnByLabel,
  onTurnOffSync,
  turnOffSyncLabel,
  syncLabel,
  syncingLabel,
  followingLabel,
  endAt = null,
  endInset = 0,
}: StageContentBarProps) {
  const shown = items.length >= 2
  const rowRef = useRef<HTMLDivElement>(null)
  // How far in from the end of the row's space the panel stops.
  const [panelInset, setPanelInset] = useState(0)
  useLayoutEffect(() => {
    const row = rowRef.current
    const space = row?.parentElement
    if (!row || !space || !endAt) {
      setPanelInset(0)
      return
    }
    const measure = () => {
      const panel = endAt.getBoundingClientRect()
      if (panel.width === 0) {
        setPanelInset(0)
        return
      }
      const spaceEnd = space.getBoundingClientRect().right - parseFloat(getComputedStyle(space).paddingRight || '0')
      setPanelInset(Math.max(0, Math.round(spaceEnd - panel.right)))
    }
    measure()
    // The panel's edge moves when the rail is dragged, a side panel opens or
    // the window resizes; each of those resizes the panel or the space.
    const observer = new ResizeObserver(measure)
    observer.observe(endAt)
    observer.observe(space)
    return () => observer.disconnect()
  }, [endAt, shown])

  if (!shown) return null
  const inset = Math.max(panelInset, endInset)

  return (
    <div
      ref={rowRef}
      className="flex items-center gap-2 px-3 shrink-0"
      // No end padding while inset: the row's edge is then where its end
      // controls stop.
      style={inset > 0 ? { marginRight: inset, paddingRight: 0 } : undefined}
    >
      {/* The tabs scroll; the end controls beside them never do. Padded
          here rather than on the row: a scroller clips its children's rings.
          No scrollbar: it added height under the tabs and dropped the end
          controls out of line with them. A tab cut off at the edge shows
          there is more, and a mouse wheel scrolls it sideways. */}
      <div
        className="scrollbar-hide flex min-w-0 flex-1 items-center gap-2 overflow-x-auto px-px py-2"
        onWheel={(e) => {
          if (e.deltaX === 0 && e.deltaY !== 0) e.currentTarget.scrollLeft += e.deltaY
        }}
      >
        {items.map((item) => {
          const Icon = ICONS[item.kind]
          const isActive = item.key === activeKey
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelect(item.key)}
              aria-pressed={isActive}
              title={item.label}
              className={[
                'flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium',
                'whitespace-nowrap transition-colors shrink-0 ring-1',
                isActive
                  ? 'bg-primary text-white ring-primary'
                  : 'bg-card/10 text-white/70 ring-white/10 hover:bg-card/20 hover:text-white',
              ].join(' ')}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {/* The label is the only thing that identifies one screen share
                  from another, so it is allowed to be long and is clipped rather
                  than dropped at narrow widths. */}
              <span className="max-w-[10rem] truncate">{item.label}</span>
            </button>
          )
        })}
      </div>

      <div className="flex items-center gap-2 py-2 shrink-0">
        {syncOnByLabel !== null ? (
          onTurnOffSync ? (
            <button
              type="button"
              onClick={onTurnOffSync}
              aria-pressed
              title={turnOffSyncLabel}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium hover:bg-primary/90 ${SYNC_ON_CLASSES}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{syncOnByLabel}</span>
              <X className="h-3 w-3" />
            </button>
          ) : (
            <span
              title={syncOnByLabel}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${SYNC_ON_CLASSES}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{syncOnByLabel}</span>
            </span>
          )
        ) : (following || followingLocked) && (
          followingLocked ? (
            <span
              title={followingLabel}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${SYNC_ON_CLASSES}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{followingLabel}</span>
            </span>
          ) : (
            <button
              type="button"
              onClick={onStopFollowing}
              title={followingLabel}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${FOLLOWING_CLASSES}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{followingLabel}</span>
              <X className="h-3 w-3" />
            </button>
          )
        )}
        {canSync && (
          <button
            type="button"
            onClick={onSync}
            aria-pressed={syncActive}
            title={syncActive ? syncingLabel : syncLabel}
            className={[
              'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
              syncActive
                ? `${SYNC_ON_CLASSES} hover:bg-primary/90`
                : 'bg-card/10 text-white/80 ring-1 ring-white/10 hover:bg-card/20 hover:text-white',
            ].join(' ')}
          >
            <Users className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{syncActive ? syncingLabel : syncLabel}</span>
          </button>
        )}
      </div>
    </div>
  )
}
